import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { ApiResponse } from '../types';
import { env } from '../utils/env';

// Base query params for project-scoped module APIs
export interface ProjectQueryParams {
  projectId: string;
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// Widget data params for dashboard API
export interface WidgetDataParams {
  key: string;
  params?: Record<string, any>;
  projectId?: string;
}

// Helper to create cache keys for project-scoped widget data
export const createProjectCacheKey = (key: string, projectId?: string): string => {
  return projectId ? `${key}_${projectId}` : key;
};

// RTK Query Base API for standardized API calls
export const baseApi = createApi({
  reducerPath: 'api',
  baseQuery: fetchBaseQuery({
    baseUrl: env.API_BASE_URL,
    credentials: 'include',
    prepareHeaders: headers => {
      const token = localStorage.getItem('auth_token');
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: [
    'Project',
    'Task',
    'Dashboard',
    'Widget',
    'Widgets',
    'WidgetData',
    'Dashboards',
    'DefaultDashboard',
    'UserDashboardPreference',
    'UserPrefs',
    'Team',
    'TeamMember',
    'Invitations',
    'UserInvitations',
    'Budget',
    'BudgetStats',
    'BudgetAlert',
    'Schedule',
    'ScheduleStats',
    'ScheduleBaseline',
    'ScheduleSettings',
    'Baseline',
    'Report',
    'Module',
    'PurchaseOrder',
    'Bill',
    'Invoice',
    'ClientInvoice',
    'ChangeOrder',
    'TimeEntry',
    'DailyLog',
    'Todo',
    'RFI',
    'Estimate',
    'EstimateLineItem',
    'EstimateMarkup',
    'EstimateSummary',
    'Profile',
    'ProfileOrganizations',
    'ProfilePermissions',
    'ProfileActivity',
    'ProfilePreferences',
    'Vendor',
    'CostCode',
    'Submittal',
    'Incident',
    'PunchItem',
    'Timesheet',
    'Milestone',
    'SafetyInspection',
    'ToolboxTalk',
    'SafetyObservation',
    'SafetyStats',
    'QualityReport',
    'QualityReportStats',
    'FieldReport',
    'FieldReportStats',
    'BidRequest',
    'BidRequestStats',
    'Proposal',
    'ProposalStats',
    'Selection',
    'SelectionStats',
    'Equipment',
    'EquipmentStats',
    'Compliance',
    'ComplianceStats',
    'Delivery',
    'DeliveryStats',
    'TaskStatistics',
    'TaskAssignableUsers',
    'TaskConfigurations',
    'TaskProgressHistory',
    'Takeoff',
    'TakeoffMeasurement',
    'TakeoffAssembly',
    'TakeoffSummary',
    'CalendarEvent',
    'PdfTemplate',
  ],
  endpoints: () => ({}),
});

// Create axios instance with default configuration
const api: AxiosInstance = axios.create({
  baseURL: env.API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Create public API instance (no auth headers)
export const publicApi: AxiosInstance = axios.create({
  baseURL: env.API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('auth_token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  error => {
    // Handle common error scenarios
    if (error.response?.status === 401) {
      // Unauthorized - remove token and redirect to login
      localStorage.removeItem('auth_token');
      window.location.href = '/login';
    }

    if (error.response?.status === 403) {
      // Forbidden - show error message
      console.error('Access denied');
    }

    if (error.response?.status >= 500) {
      // Server error
      console.error('Server error occurred');
    }

    return Promise.reject(error);
  }
);

// Generic API methods
export const apiService = {
  // GET request
  get: <T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
    return api.get(url, config).then(response => response.data);
  },

  // POST request
  post: <T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
    return api.post(url, data, config).then(response => response.data);
  },

  // PUT request
  put: <T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
    return api.put(url, data, config).then(response => response.data);
  },

  // PATCH request
  patch: <T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
    return api.patch(url, data, config).then(response => response.data);
  },

  // DELETE request
  delete: <T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
    return api.delete(url, config).then(response => response.data);
  },

  // File upload
  upload: <T>(
    url: string,
    formData: FormData,
    config?: AxiosRequestConfig
  ): Promise<ApiResponse<T>> => {
    return api
      .post(url, formData, {
        ...config,
        headers: {
          'Content-Type': 'multipart/form-data',
          ...config?.headers,
        },
      })
      .then(response => response.data);
  },
};

// Export both as named export and default export for flexibility
export { api };
export default api;
